import { getIdToken } from "src/domains/shared/utils/amplifyCompat";
import { backendBaseUrl } from "src/domains/shared/api/calculation/backendBaseUrl";
import {
  SIGNUP_CONFIRM_PATH,
  SIGNUP_PATH,
  SIGNUP_RESEND_PATH,
  SIGNUP_RETRY,
  USERINFO_PATH,
  classifyRegistrationResponse,
  classifySignupResponse,
  type RegistrationStatus,
  type SignupOutcome,
  type SignupRequestBody,
} from "./signup.model";

/** main-api URL(`backendBaseUrl()` + path) — 가입·확정·재전송 세 POST 와 등록 상태 조회(`GET users/userinfo`)가 함께 쓴다. */
export function mainApiUrl(path: string): string {
  return `${backendBaseUrl()}/${path}`;
}

/** 판정에 쓰는 본문은 실패 응답의 것뿐이다(4xx 의 `code`). 성공 본문은 읽지 않는다. 본문을 읽지 못하면 `code` 가 없는 것으로 본다. */
async function readFailureBody(response: Response): Promise<string | null> {
  if (response.ok) return null;
  try {
    return await response.text();
  } catch {
    return null;
  }
}

/**
 * 무인증 `POST` + JSON 본문 하나. throw 하지 않는다 — 네트워크 실패를 포함한 모든 실패는 판정 결과로 돌려준다.
 * `Authorization` 을 싣지 않는다 — 세 라우트는 가입 중인(아직 로그인하지 않은) 사용자의 표면이다.
 */
async function postSignup(path: string, body: unknown): Promise<SignupOutcome> {
  try {
    const response = await fetch(mainApiUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return classifySignupResponse(response.status, await readFailureBody(response));
  } catch {
    return SIGNUP_RETRY;
  }
}

/** 가입 요청 — 완료면 IdP 가 인증코드 메일을 보낸 것이다. */
export function requestSignup(body: SignupRequestBody): Promise<SignupOutcome> {
  return postSignup(SIGNUP_PATH, body);
}

/**
 * 가입 완료 — 미확정 계정은 `code` 로 확정한 뒤 등록까지 끝내고, 이미 확정된 계정은 등록만 이어서 끝낸다(재진입 — 그때는
 * `code` 없이 부른다). 같은 이메일·코드로 다시 보내도 안전하다.
 */
export function confirmSignup({ email, code }: { email: string; code?: string }): Promise<SignupOutcome> {
  return postSignup(SIGNUP_CONFIRM_PATH, code === undefined ? { email } : { email, code });
}

/** 인증코드 재전송. */
export function resendSignupCode({ email }: { email: string }): Promise<SignupOutcome> {
  return postSignup(SIGNUP_RESEND_PATH, { email });
}

/**
 * 로그인한 계정의 등록 상태(`GET users/userinfo`, 현재 세션 토큰). throw 하지 않는다 — 네트워크 실패는 `unknown` 이다.
 * 응답 캐시를 쓰지 않는다.
 */
export async function fetchRegistrationStatus(): Promise<RegistrationStatus> {
  try {
    const response = await fetch(mainApiUrl(USERINFO_PATH), {
      method: "GET",
      headers: { Authorization: await getIdToken() },
      cache: "no-store",
    });
    return classifyRegistrationResponse(response.status, await readFailureBody(response));
  } catch {
    return "unknown";
  }
}
